import { useCallback, useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import ReactGA from 'react-ga4';

import useAuth from 'src/hooks/useAuth';
import http from 'src/services/httpService';
import guid from 'src/utils/guid';
import { getSession } from 'src/contexts/AuthContext';
import { isNullOrWhiteSpace } from 'src/utils/string';
import {
  MODULOS,
  TIPO_INTERACAO_SOPHIE_LABEL,
  TIPO_INTERACAO_SOPHIE,
} from 'src/utils/constants';
import errorHandler from 'src/utils/errorHandler';
import ContentManager from 'src/componentes/content-manager';
import FeedbackSophie from 'src/components/IA/ChatGPT/FeedbackSophie';

const emptyBotMessage = { isUser: false, isVisible: false, text: '' };
const emptyBotHistoryMessage = { role: 'assistant', content: '' };

function buildInitialHistory(systemContent, prompt, greeting) {
  const history = [{ role: 'system', content: systemContent }];
  if (prompt) {
    history.push({ role: 'system', content: prompt });
  }
  if (greeting) {
    history.push({ role: 'assistant', content: greeting });
  } else if (prompt) {
    history.push({ ...emptyBotHistoryMessage });
  }
  return history;
}

function buildInitialMessages(greeting) {
  return greeting
    ? [{ isUser: false, isVisible: true, text: greeting }]
    : [{ ...emptyBotMessage }];
}

export default function useChatIA({
  user,
  prompt,
  greeting,
  interactionType,
  usePromptButtons = false,
  listButtons = [],
  dontShowFeedbackSophie = false,
}) {
  const intl = useIntl();
  const { modules } = useAuth();

  const requestId = useMemo(() => guid(), [interactionType, user]);
  const isChatbotSuporte =
    interactionType === TIPO_INTERACAO_SOPHIE.CHATBOT_SUPORTE;
  const isDocumentInsights =
    interactionType === TIPO_INTERACAO_SOPHIE.INSIGHTS_GESTAO_DOCUMENTOS;

  const [hasSophie, setHasSophie] = useState(false);
  const [isReady, setIsReady] = useState(false);
  const [isStreaming, setIsStreaming] = useState(false);
  const [showFeedback, setShowFeedback] = useState(false);
  const [copiedMessageIndex, setCopiedMessageIndex] = useState(null);
  const [newMessage, setNewMessage] = useState('');
  const [promptButtonsVisible, setPromptButtonsVisible] = useState(
    usePromptButtons && listButtons.length > 0
  );
  const [messages, setMessages] = useState(() =>
    buildInitialMessages(greeting)
  );
  const [historyContext, setHistoryContext] = useState(
    buildInitialHistory('', prompt, greeting)
  );

  useEffect(() => {
    const label =
      TIPO_INTERACAO_SOPHIE_LABEL[interactionType] || interactionType;
    ReactGA.event(`Sophie - ${label}`, {
      category: 'sophie_gpt',
      label: user?.nome,
    });
  }, []);

  useEffect(() => {
    async function fetchInfo() {
      try {
        const infoPrompt = await http.post(
          `/ConfiguracaoConsultoraVirtual/${
            isChatbotSuporte
              ? 'ObterPromptChatbotSuporte'
              : 'ObterPromptSistema'
          }`
        );

        const hasLicenceSophie = modules.find(
          (m) => m.id === MODULOS.INTELIGENCIA_ARTIFICIAL && m.visivel
        );

        setHasSophie(!!hasLicenceSophie);
        setHistoryContext(
          buildInitialHistory(infoPrompt?.data, prompt, greeting)
        );
        setMessages(buildInitialMessages(greeting));
      } catch (error) {
        errorHandler(error, intl);
        setHistoryContext(buildInitialHistory('', prompt, greeting));
        setMessages(buildInitialMessages(greeting));
      } finally {
        setIsReady(true);
      }
    }

    fetchInfo();
  }, []);

  useEffect(() => {
    async function fetchOnboardAccess() {
      try {
        const response = await http.post(
          '/FeedbackActioIA/ObterDadosAcessoFeedback',
          { interactionType }
        );
        setShowFeedback(!!response?.data);
      } catch (err) {
        errorHandler(err, intl);
      }
    }

    fetchOnboardAccess();
  }, []);

  const appendBotChunk = useCallback((chunk) => {
    setMessages((prev) => {
      const next = [...prev];
      const last = { ...next[next.length - 1] };
      last.isVisible = true;
      last.text += chunk;
      next[next.length - 1] = last;
      return next;
    });

    setHistoryContext((prev) => {
      const next = [...prev];
      const last = { ...next[next.length - 1] };
      last.content += chunk;
      next[next.length - 1] = last;
      return next;
    });
  }, []);

  const streamBotResponse = useCallback(async () => {
    setIsStreaming(true);
    const { accessToken } = getSession();

    let response;
    try {
      response = await fetch(
        process.env.REACT_APP_API_OPENAI + '/api/open-ai',
        {
          method: 'POST',
          body: JSON.stringify({
            messages: historyContext,
            interactionType,
            requestId,
            hasLicence: hasSophie,
            module: isChatbotSuporte ? 'GE' : undefined,
          }),
          headers: {
            'content-type': 'application/json',
            Authorization: `Bearer ${accessToken}`,
          },
        }
      );
    } catch (err) {
      setMessages((prev) => prev.slice(0, -1));
      setIsStreaming(false);
      errorHandler(err, intl);
      return;
    }

    if (!response.ok) {
      let errorCode;
      try {
        const error = await response.json();
        errorCode = error?.code;
      } catch (parseErr) {
        errorHandler(parseErr, intl);
      }

      setMessages((prev) => {
        const withoutPending = prev.slice(0, -1);
        if (errorCode === 'LIMIT_EXCEEDED') {
          const msgPlano = hasSophie
            ? ''
            : intl.formatMessage({ id: 'planoGratuitoActioIA' });
          const msgErro = intl
            .formatMessage({ id: 'msgErroLimiteTokensAtingido' })
            .replace('{planoGratuito}', msgPlano);
          return [
            ...withoutPending,
            { isUser: false, isVisible: true, text: msgErro },
          ];
        }
        const msgErro = intl.formatMessage({ id: 'label.ocorreuUmErro' });
        return [
          ...withoutPending,
          { isUser: false, isVisible: true, text: msgErro },
        ];
      });

      setIsStreaming(false);
      return;
    }

    const reader = response.body?.getReader();
    if (!reader) {
      setMessages((prev) => prev.slice(0, -1));
      setIsStreaming(false);
      return;
    }

    const decoder = new TextDecoder();
    let documentInsightsResponse = '';
    let done = false;

    try {
      while (!done) {
        const { value, done: streamDone } = await reader.read();
        if (value) {
          const chunk = decoder.decode(value, { stream: true });
          if (isDocumentInsights) {
            documentInsightsResponse += chunk;
          } else {
            appendBotChunk(chunk);
          }
        }
        done = streamDone;
      }

      const finalChunk = decoder.decode();
      if (isDocumentInsights) {
        documentInsightsResponse += finalChunk;
        let formattedResponse;
        try {
          formattedResponse = formatDocumentInsightsResponse(
            JSON.parse(documentInsightsResponse)
          );
        } catch (error) {
          errorHandler(error, intl);
          formattedResponse = intl.formatMessage({
            id: 'label.ocorreuUmErro',
          });
        }

        setMessages((prev) => {
          const lastIndex = prev.length - 1;
          return prev.map((message, index) =>
            index === lastIndex
              ? { ...message, isVisible: true, text: formattedResponse }
              : message
          );
        });
        setHistoryContext((prev) => {
          const lastIndex = prev.length - 1;
          return prev.map((message, index) =>
            index === lastIndex
              ? { ...message, content: formattedResponse }
              : message
          );
        });
      } else if (finalChunk) {
        appendBotChunk(finalChunk);
      }
    } finally {
      setIsStreaming(false);
    }
  }, [
    historyContext,
    interactionType,
    requestId,
    hasSophie,
    isDocumentInsights,
    appendBotChunk,
    intl,
  ]);

  useEffect(() => {
    if (!isReady || promptButtonsVisible) return;
    if (isChatbotSuporte && messages.length === 1) return;

    const lastMessage = messages[messages.length - 1];
    if (lastMessage && !lastMessage.isUser && !lastMessage.isVisible) {
      streamBotResponse();
    }
  }, [messages, isReady, promptButtonsVisible, isChatbotSuporte]);

  const pushUserMessage = (text, historyContent) => {
    setHistoryContext((prev) => [
      ...prev,
      { role: 'user', content: historyContent },
      { ...emptyBotHistoryMessage },
    ]);

    setMessages((prev) => [
      ...prev,
      { isUser: true, isVisible: true, text },
      { ...emptyBotMessage },
    ]);
  };

  const sendMessage = useCallback(() => {
    if (isNullOrWhiteSpace(newMessage)) return;

    setPromptButtonsVisible(false);
    pushUserMessage(newMessage, newMessage);
    setNewMessage('');
  }, [newMessage]);

  const handlePromptButtonClick = useCallback((promptText, buttonName) => {
    setPromptButtonsVisible(false);
    setNewMessage('');
    pushUserMessage(buttonName, promptText);
  }, []);

  const copyMessage = useCallback((text, index) => {
    navigator.clipboard.writeText(text);
    setCopiedMessageIndex(index);
    setTimeout(() => setCopiedMessageIndex(null), 2000);
  }, []);

  const collectFeedback = useCallback(() => {
    ContentManager.addContent(
      <FeedbackSophie
        requestId={requestId}
        subdomain={window?.location?.hostname.split('.')[0] || null}
        userId={user?.id}
        interactionType={interactionType}
      />
    );
  }, [requestId, user?.id, interactionType]);

  const hasInteraction = useMemo(
    () => messages.some((msg) => msg.isUser),
    [messages]
  );

  const requestClose = useCallback(() => {
    if (!dontShowFeedbackSophie && showFeedback && hasInteraction) {
      collectFeedback();
    }
  }, [dontShowFeedbackSophie, showFeedback, hasInteraction, collectFeedback]);

  function formatDocumentInsightsResponse(response) {
    const references = response.referencias || [];
    const referenceList = references
      .map(
        (reference) =>
          `- [${
            reference.documentName
          }](/gerenciar/gestao-documentos?documentoId=${encodeURIComponent(
            reference.actioDocumentID
          )}) | ${intl.formatMessage({ id: 'idIntegracao' })}: ${
            reference.documentCode
          }`
      )
      .join('\n');

    return [
      response.resposta,
      referenceList &&
        `${intl.formatMessage({
          id: 'labelDocumentosReferencia',
        })}:\n${referenceList}`,
    ]
      .filter(Boolean)
      .join('\n\n');
  }

  return {
    messages,
    historyContext,
    isReady,
    isStreaming,
    promptButtonsVisible,
    newMessage,
    setNewMessage,
    sendMessage,
    handlePromptButtonClick,
    copiedMessageIndex,
    copyMessage,
    requestClose,
  };
}
